/**
 * @fileoverview A collection's keys: where each one lives, and which event may
 * be destroyed without taking a page down with it.
 *
 * The cryptography is not here. It is `@cloistr/auth`, shared with the file
 * service so that one wire format exists rather than two. What IS here is the
 * part that is specific to pages: WHICH KEY GOES IN WHICH EVENT, and that
 * placement is the whole mechanism rather than a detail.
 *
 * ---
 *
 * THREE PLACES A KEY LIVES, AND THEY ARE NOT INTERCHANGEABLE.
 *
 *   on the PAGE's own event    the page key, wrapped to the OWNER
 *   in the COLLECTION event    each member's key, wrapped under the collection key
 *   on a SHARE event           the collection key, wrapped to one recipient
 *
 * THE FIRST ONE IS THE LOAD-BEARING PLACEMENT AND IT IS COUNTER-INTUITIVE.
 * The obvious design puts the owner's copy of every key in the collection,
 * because that is where the owner looks. That version protects nothing.
 * A collection event is addressable and replaceable, so a bad write replaces it
 * and two devices editing concurrently will lose one of them. If the owner's
 * only route to a page key was inside the collection, destroying the collection
 * would leave the page's ciphertext sitting in Blossom openable by nobody,
 * including its author, permanently.
 *
 * A container cannot take down what it does not carry. So the owner's envelope
 * travels with the PAGE.
 *
 * THE COLLECTION'S OWN KEY IS THE EXCEPTION, AND IT IS SAFE. Its owner envelope
 * does sit on the collection event, which looks like the mistake above and is
 * not: if that event is gone, the collection is gone, and the only thing lost is
 * the grouping. Every page in it is still openable from its own event. Losing a
 * container that carries nothing irreplaceable costs nothing irreplaceable.
 *
 * ---
 *
 * THE SUBJECT IS THE PAGE'S ADDRESS, WHICH IS THE ONE NAME THAT NEVER CHANGES.
 *
 * Every envelope names what it opens, and unwrapping refuses a mismatch. The
 * name used here is the addressable coordinate, because that is the only
 * identifier a page keeps for its whole life: not the event id, which changes on
 * every edit, and not the blob hash, which changes on every edit of the body.
 *
 * Binding to anything that changes would mean every envelope in a collection had
 * to be rewritten whenever any page was edited, which would hand back exactly
 * the write-amplification problem that wrapping exists to avoid.
 *
 * ---
 *
 * THE MEMBER KIND IS READ, NEVER ASSUMED. A member is identified by whatever
 * coordinate its own address carries. At the time of writing, the collection
 * spec still describes members as long-form events in seven places, which was
 * true when it was written and is not true now that every page is our own kind.
 * Nothing here hardcodes a kind, so that correction cannot reach this file.
 */

import {
  type ContentKey,
  type Envelope,
  type KeySetEntry,
  generateContentKey,
  openKeySet,
  sealKeySet,
  unwrapKeyFromSender,
  wrapKeyForRecipient,
} from '@cloistr/auth/core';
import type { SignerInterface } from '@cloistr/auth/core';

/**
 * The tag carrying a key wrapped to the owner.
 *
 * One name for both cases, a page's own key on its page and a collection's key
 * on its collection, because they are the same thing: the owner being a
 * recipient of their own data by the ordinary mechanism rather than a special
 * case.
 */
export const OWNER_KEY_TAG = 'key';

/**
 * THERE IS NO MEMBER KEY TAG, AND THE ABSENCE IS THE DESIGN.
 *
 * A member key row pairs an address with the fact that the page at it is
 * encrypted. For a member that is hidden, putting that pair in a tag names the
 * member in the clear and undoes the whole reason the sealed half exists. For a
 * member that is public, it is free to seal anyway, and one rule is safer than
 * a rule with an exception a later reader has to re-derive.
 *
 * So every member key travels INSIDE the collection's encrypted half. This
 * constant used to be `mkey` and its rows used to be tags, filtered to public
 * members at the point of writing. That filter silently DROPPED a private
 * member's key, which is how the whole layer came to be built and wired to
 * nothing.
 */

/** A page in a collection: its permanent address, and the key that opens it. */
export interface CollectionMember {
  /** The addressable coordinate, `kind:pubkey:d`. Never an event id. */
  address: string;
  key: ContentKey;
}

/**
 * A fresh key for a collection.
 *
 * Separate from a page key in name only; both are 32 random bytes and neither is
 * derived from anything. The distinct name is so that a reader of a call site
 * can tell which level they are at, because the whole design is about the two
 * levels moving independently.
 */
export function generateCollectionKey(): ContentKey {
  return generateContentKey();
}

/**
 * A new key for a member that is being un-shared.
 *
 * SAME FUNCTION AS GENERATING ANY KEY, named for what it is FOR, because the
 * operation around it is the one people get wrong. Un-sharing one member means
 * rotating that member's key, re-encrypting THAT ONE BLOB, and rewriting the
 * collection event. It does NOT mean touching the collection key, and if anyone
 * reaches for that, every other recipient is disturbed for one member's sake.
 */
export function rotatedMemberKey(): ContentKey {
  return generateContentKey();
}

/**
 * The owner's envelope for a key, to sit on the item the key opens.
 *
 * Costs a signer round trip, which on a remote signer is a prompt on somebody's
 * phone. That is once per PAGE at publish time, not once per reader and not once
 * per collection edit.
 */
export async function ownerKeyTag(
  key: ContentKey,
  address: string,
  signer: SignerInterface
): Promise<[string, Envelope]> {
  const ownerPubkey = await signer.getPublicKey();
  return [OWNER_KEY_TAG, await wrapKeyForRecipient(key, address, ownerPubkey, signer)];
}

/**
 * Recover a key from the owner's envelope on an item's own event.
 *
 * THIS IS THE PATH THAT MUST KEEP WORKING WITH THE COLLECTION EVENT GONE, which
 * is why it takes an item's tags and knows nothing about collections at all.
 *
 * Returns null when there is no such tag, because an item published before this
 * existed, or a public item that never needed a key, is an ordinary case and not
 * an error. A tag that IS present and does not open is an error and throws: that
 * means the envelope was tampered with or belongs to something else, and
 * treating it as absent would silently downgrade a real failure into a blank.
 */
export async function readOwnerKey(
  tags: readonly (readonly string[])[],
  address: string,
  signer: SignerInterface
): Promise<ContentKey | null> {
  // The envelope is pulled out by value rather than indexed again at the call,
  // because a `find` predicate narrows inside itself and not afterwards, and an
  // index that is merely PROBABLY a string is how a tag row with a missing cell
  // reaches the crypto as undefined.
  let envelope: string | null = null;
  for (const t of tags) {
    if (t[0] === OWNER_KEY_TAG && typeof t[1] === 'string') {
      envelope = t[1];
      break;
    }
  }
  if (envelope === null) return null;
  const ownerPubkey = await signer.getPublicKey();
  return unwrapKeyFromSender(envelope, address, ownerPubkey, signer);
}

/**
 * Every member's key, wrapped under the collection key, for the SEALED half.
 *
 * SYMMETRIC, SO NO SIGNER AND NO PROMPT. This is the operation that runs once
 * per member, and keeping it off the signer is the entire economic argument for
 * two layers: adding a page to a shared collection rewrites one small event and
 * asks nobody for anything.
 *
 * WRAPPED EVEN THOUGH THE HALF IT SITS IN IS ALREADY ENCRYPTED UNDER THE SAME
 * KEY, which looks like belt and braces and is not. An envelope NAMES WHAT IT
 * OPENS, so a row whose address was swapped is refused rather than handing back
 * the wrong page's key. Without that binding, the member list and the key list
 * are two parallel arrays that nothing forces to agree, which is the same shape
 * of defect `assertCollectionConsistent` exists to catch one level up.
 */
export function sealMemberKeys(
  members: readonly CollectionMember[],
  collectionKey: ContentKey
): KeySetEntry[] {
  return sealKeySet(
    members.map((m) => ({ subject: m.address, key: m.key })),
    collectionKey
  );
}

/**
 * Open every member key a collection key can open.
 *
 * ROWS THAT DO NOT OPEN ARE REPORTED, NOT THROWN, and that is deliberate. A
 * collection is a list its owner may have rewritten since you last read it, so
 * one unreadable row means one page you no longer have access to. Throwing would
 * deny the reader the other thirty-nine and show an empty site.
 *
 * The caller is told which ones failed so it can say so, rather than quietly
 * rendering a shorter list than the collection actually has.
 */
export function openMemberKeys(
  rows: unknown,
  collectionKey: ContentKey
): { keys: Map<string, ContentKey>; unopened: string[] } {
  // TAKEN AS `unknown` BECAUSE THE ROWS COME OUT OF JSON THAT A RECIPIENT MAY
  // HAVE WRITTEN. The sealed half is parsed from ciphertext this reader decrypted,
  // which proves it was sealed under this key and proves nothing about its shape.
  // A row missing a cell would otherwise reach the crypto as undefined.
  const entries: KeySetEntry[] = [];
  if (Array.isArray(rows)) {
    for (const row of rows as unknown[]) {
      if (typeof row !== 'object' || row === null) continue;
      const { subject, envelope } = row as Partial<KeySetEntry>;
      if (typeof subject === 'string' && typeof envelope === 'string') {
        entries.push({ subject, envelope });
      }
    }
  }
  return openKeySet(entries, collectionKey);
}

/**
 * The collection key, wrapped to one person.
 *
 * ONE CALL, WHATEVER THE MEMBER COUNT. Sharing a site of forty pages is one
 * signer round trip, not forty, and that difference is what makes the feature
 * usable on a remote signer rather than merely possible.
 */
export async function shareCollectionKey(
  collectionKey: ContentKey,
  collectionAddress: string,
  recipientPubkey: string,
  signer: SignerInterface
): Promise<Envelope> {
  return wrapKeyForRecipient(collectionKey, collectionAddress, recipientPubkey, signer);
}

/**
 * Open a collection key that was shared with you.
 *
 * `ownerPubkey` is whose key the exchange is against, which for an owner opening
 * their own envelope is their own.
 */
export async function openSharedCollectionKey(
  envelope: Envelope,
  collectionAddress: string,
  ownerPubkey: string,
  signer: SignerInterface
): Promise<ContentKey> {
  return unwrapKeyFromSender(envelope, collectionAddress, ownerPubkey, signer);
}
